/**
 * «Editar producto»: the product's own properties, changed together in one sheet and saved as one
 * `producto.actualizado` that carries only what changed (the owner, 2026-10-04). Here rather than
 * in the sheet, so what a save sends, and what stops it, is tested without a screen.
 */
import type { Existencia, Producto } from '@abastece/core';
import { formatQuantity, parseQuantity, type Milli, type PayloadInputOf } from '@abastece/shared';
import { normalizar } from './texto.js';

/** The sheet's fields as they are typed: text where a person types, the rest as it is kept. */
export interface BorradorProducto {
  nombre: string;
  nombreEn: string;
  categoria: string;
  unidadBase: string;
  esMedicamento: boolean;
  registroSanitario: string;
  perecedero: boolean;
  /** In the product's unit. Empty: none, and «Por comprar» goes by how fast it sells. */
  stockMinimo: string;
}

/** The properties a save changes, in the event's own words: `productoId` aside. */
export type CambiosDeProducto = Omit<PayloadInputOf<'producto.actualizado'>, 'productoId'>;

/** The sheet as it opens: what the product has now. */
export function borradorDe(p: Producto): BorradorProducto {
  return {
    nombre: p.nombre,
    nombreEn: p.nombreEn ?? '',
    categoria: p.categoria,
    unidadBase: p.unidadBase,
    esMedicamento: p.esMedicamento,
    registroSanitario: p.registroSanitario ?? '',
    perecedero: p.perecedero,
    stockMinimo: p.stockMinimo != null ? formatQuantity(p.stockMinimo) : '',
  };
}

/** The minimum as typed: a quantity, null for none, or undefined when it is not one. */
function minimoDe(texto: string): Milli | null | undefined {
  if (!texto.trim()) return null;
  const valor = parseQuantity(texto);
  return valor === null || valor < 0 ? undefined : valor;
}

/** A minimum that is not a quantity, or is below zero: said under its field, and nothing is saved. */
export function minimoInvalido(texto: string): boolean {
  return minimoDe(texto) === undefined;
}

/**
 * What a save sends: each property whose value is not what the product has, and nothing else.
 *
 * Compared as they are kept, with spaces at either end trimmed off, so a sheet opened and saved
 * untouched sends nothing. An emptied category is «Otros», as a new product's is; an emptied English
 * name is cleared, and the English screen goes back to the Spanish one. The MINSA registration is
 * only read while the product is medicine: switched off, the one it had is kept, out of sight, for
 * whenever it is switched back on. An empty name and a minimum that is not a quantity are what stop
 * a save (`otroConEseNombre`, `minimoInvalido`), and send nothing here.
 */
export function cambiosDe(p: Producto, b: BorradorProducto): CambiosDeProducto {
  const cambios: CambiosDeProducto = {};
  const nombre = b.nombre.trim();
  if (nombre && nombre !== p.nombre.trim()) cambios.nombre = nombre;
  const nombreEn = b.nombreEn.trim();
  if (nombreEn !== (p.nombreEn ?? '').trim()) cambios.nombreEn = nombreEn || null;
  const categoria = b.categoria.trim() || 'Otros';
  if (categoria !== p.categoria.trim()) cambios.categoria = categoria;
  if (b.unidadBase !== p.unidadBase) cambios.unidadBase = b.unidadBase;
  if (b.esMedicamento !== p.esMedicamento) cambios.esMedicamento = b.esMedicamento;
  if (b.esMedicamento) {
    const registro = b.registroSanitario.trim();
    if (registro !== (p.registroSanitario ?? '').trim()) cambios.registroSanitario = registro || null;
  }
  if (b.perecedero !== p.perecedero) cambios.perecedero = b.perecedero;
  const minimo = minimoDe(b.stockMinimo);
  if (minimo !== undefined && minimo !== (p.stockMinimo ?? null)) cambios.stockMinimo = minimo;
  return cambios;
}

/** A name as two people would both write it: accents, case and spacing apart. */
const comoNombre = (s: string) => normalizar(s).replace(/\s+/g, ' ');

/**
 * Another product on sale that already goes by the name typed, to be said under the name: two of
 * one product split its stock and its sales in two. Only once the name is another than the one it
 * had, so a product that already shared its name is not held up over a change to something else.
 * One off sale is not in the way: whoever puts it back can rename it then.
 */
export function otroConEseNombre(productos: readonly Producto[], producto: Producto, nombre: string): Producto | undefined {
  const buscado = comoNombre(nombre);
  if (!buscado || buscado === comoNombre(producto.nombre)) return undefined;
  return productos.find((p) => p.id !== producto.id && p.activo && comoNombre(p.nombre) === buscado);
}

/**
 * The stock that changes unit with the product, for the line under the unit that says only the
 * name changes: what is counted now, in every place together. Null while the unit is the one it
 * had, or nothing is counted. The rule itself is the books' (`reduceProducto` in the core).
 */
export function existenciaQueCambiaDeUnidad(p: Producto, b: BorradorProducto, existencias: readonly Existencia[]): Milli | null {
  if (b.unidadBase === p.unidadBase) return null;
  const cantidad = existencias.reduce((suma, e) => suma + e.cantidad, 0);
  return cantidad === 0 ? null : (cantidad as Milli);
}
